import React, { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import * as Tabs from '@radix-ui/react-tabs';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import TabsList from './TabsList';
import Actions from './Actions';
import Controls from './Controls';
import Filters from './Filters';
import PlotCard from './PlotCard';
import Key from './Key';
import Marker from './Marker';
import Hotspot from './Hotspot';
import Floorplan from './Floorplan';
import { CloseIcon, LeftArrow, filtersCount, useWindowDimensions } from './lib';

export default function FullScreenPlan({
  activeFilters,
  activeFloorplan,
  bedrooms,
  heading,
  fullScreen,
  isDesktop,
  maxPrice,
  propType,
  resetFilters,
  setActiveFilters,
  setActiveFloorplan,
  setBedrooms,
  setFullScreen,
  setMaxPrice,
  setPropType,
  setShowFilters,
  setStatus,
  setReadyToMoveInto,
  showFilters,
  sitePlans,
  status,
  readyToMoveInto,
  subheading = '',
}) {
  // Set the plot card when a user clicks on a marker
  const [activePlot, setActivePlot] = useState(undefined);
  // Logic to toggle wether or not to show the plot card
  const [cardOpen, setCardOpen] = useState(false);
  // Full screen zoom controls
  const minZoom = 0.75;
  const maxZoom = 3;
  // The height of the browser window (as this is full screen)
  const { height } = useWindowDimensions();

  // Remove 200px from the height as there is a fixed menu bar above the map area
  const adjustedHeight = height - 200;
  // Take the height and divide it by 640, as in the CMS our map is 792px wide and 640px high,
  // so we need to calculate the aspect ratio
  const adjustY = adjustedHeight / 640;
  // Given that we know that 792 divided by 640 is 1.2375 - this is our aspect ratio, so we need to divide
  // the available map height by this ratio to get the aspect ratio for the width of the map
  const adjustX = (adjustedHeight * 1.2375) / 792;

  return (
    <Dialog.Root
      open={fullScreen}
      onOpenChange={(newOpen) => setFullScreen(newOpen)}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fullscreen__overlay">
          <Dialog.Content className="fullscreen__content">
            <div className="fullscreen__outer">
              <div className="container fullscreen__container">
                <div className="fullscreen__row">
                  <div className="fullscreen__col fullscreen__col--left">
                    <div className="fullscreen__details">
                      {heading && (
                        <h2 className="fullscreen__heading">{heading}</h2>
                      )}

                      {subheading && (
                        <p className="fullscreen__subheading">{subheading}</p>
                      )}

                      <Dialog.Close asChild>
                        <button
                          aria-label="Close"
                          className="fullscreen__close"
                          type="button"
                        >
                          <CloseIcon className="fullscreen__close-icon" />
                          <LeftArrow className="fullscreen__left-arrow" />
                        </button>
                      </Dialog.Close>
                    </div>
                  </div>

                  <div className="fullscreen__col fullscreen__col--right">
                    <TabsList
                      className="fullscreen__tabs"
                      resetFilters={resetFilters}
                      setActiveFloorplan={setActiveFloorplan}
                      sitePlans={sitePlans}
                    />
                  </div>
                </div>
              </div>
              <div className="siteplan__maps fullscreen__maps">
                {sitePlans?.map((plan) => (
                  <Tabs.Content
                    className="fullscreen__tab"
                    key={plan.name}
                    value={plan.name}
                  >
                    <div className="fullscreen__wrapper">
                      <TransformWrapper
                        centerOnInit
                        initialScale={isDesktop ? 1 : 0.75}
                        initialPositionX={0}
                        initialPositionY={0}
                        maxScale={maxZoom}
                        minScale={minZoom}
                        wheel={{
                          disabled: true,
                        }}
                        onInit={(ref) => {
                          ref.instance.setup.panning.disabled = false;
                        }}
                      >
                        {({ zoomIn, zoomOut }) => (
                          <>
                            <Actions
                              activeFloorplan={activeFloorplan}
                              className="fullscreen__actions"
                              filterCount={filtersCount(activeFilters)}
                              minZoom={minZoom}
                              plan={plan}
                              setActiveFloorplan={setActiveFloorplan}
                              setActiveFilters={setActiveFilters}
                              setShowFilters={setShowFilters}
                              zoomOut={zoomOut}
                            />
                            <Controls
                              className="fullscreen__controls"
                              fullScreen={fullScreen}
                              setFullScreen={setFullScreen}
                              zoomIn={zoomIn}
                              zoomOut={zoomOut}
                            />

                            {plan.type !== 'apartment' && plan.image && (
                              <TransformComponent>
                                <div
                                  className="fullscreen__pan"
                                  style={{
                                    width: adjustedHeight * 1.2375,
                                    height: adjustedHeight,
                                  }}
                                >
                                  <div
                                    className="siteplan__map"
                                    style={{
                                      backgroundImage: `url(${plan.image})`,
                                    }}
                                  >
                                    {plan.markers.map((plot) => (
                                      <div key={plot.label}>
                                        {plot?.type === 'apartment' ||
                                        plot?.type === 'hotspot' ? (
                                          <Hotspot
                                            multiplier={undefined}
                                            adjustY={adjustY}
                                            adjustX={adjustX}
                                            plot={plot}
                                            resetFilters={resetFilters}
                                          />
                                        ) : (
                                          <Marker
                                            activeFilters={activeFilters}
                                            marker={plot}
                                            multiplier={undefined}
                                            adjustY={adjustY}
                                            adjustX={adjustX}
                                            setActivePlot={setActivePlot}
                                            setCardOpen={setCardOpen}
                                          />
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </TransformComponent>
                            )}

                            {plan.type === 'apartment' &&
                              plan?.floorplans?.length > 0 && (
                                <TransformComponent>
                                  <Floorplan
                                    activeFilters={activeFilters}
                                    activeFloorplan={
                                      activeFloorplan ?? undefined
                                    }
                                    className="fullscreen__pan"
                                    height={adjustedHeight}
                                    multiplier={undefined}
                                    adjustY={adjustY}
                                    adjustX={adjustX}
                                    plans={plan.floorplans}
                                    setActivePlot={setActivePlot}
                                    setCardOpen={setCardOpen}
                                    width={adjustedHeight * 1.2375}
                                  />
                                </TransformComponent>
                              )}
                          </>
                        )}
                      </TransformWrapper>
                      <Filters
                        className="fullscreen__filters"
                        bedrooms={bedrooms}
                        maxPrice={maxPrice}
                        plan={plan}
                        propType={propType}
                        resetFilters={resetFilters}
                        setActiveFilters={setActiveFilters}
                        setBedrooms={setBedrooms}
                        setMaxPrice={setMaxPrice}
                        setShowFilters={setShowFilters}
                        setPropType={setPropType}
                        setStatus={setStatus}
                        setReadyToMoveInto={setReadyToMoveInto}
                        showFilters={showFilters}
                        status={status}
                        readyToMoveInto={readyToMoveInto}
                      />
                    </div>
                  </Tabs.Content>
                ))}
                <Key className="fullscreen__key" />
                <PlotCard
                  className="fullscreen__card"
                  plot={activePlot}
                  isOpen={cardOpen}
                  onPlotChange={setActivePlot}
                  onPlotClose={setCardOpen}
                />
              </div>
            </div>
          </Dialog.Content>
        </Dialog.Overlay>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
